import React from 'react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceArea } from 'recharts'
import { useAtom } from 'jotai'
import type { ChartConfigIdType } from '@shared/constants.js'
import { themeAtom } from '../../atoms.js'
import { useChartData } from '../../hooks/useChartData.js'
import Card from '../Card.js'
import { getChartTheme } from './chartTheme.js'

type Props = {
  chartId: ChartConfigIdType
  title: string
}

function getWeekendRanges(data: Array<{ [key: string]: any }>, year: number) {
  const ranges: Array<{ start: string; end: string }> = []
  for (const row of data) {
    const dateStr = row.date as string
    if (/^\d{2}-\d{2}$/.test(dateStr)) {
      const [month, day] = dateStr.split('-').map(Number)
      const date = new Date(year, month! - 1, day)
      const dayOfWeek = date.getDay()
      if (dayOfWeek === 6 || dayOfWeek === 0) {
        ranges.push({ start: dateStr, end: dateStr })
      }
    }
  }
  return ranges
}

export default function LineChartCard({ chartId, title }: Props) {
  const [theme] = useAtom(themeAtom)
  const { data, loading, error } = useChartData(chartId)
  const t = getChartTheme(theme)

  if (loading) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">loading...</div>
      </Card>
    )
  }

  if (error || !data) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-red-400">加载失败</div>
      </Card>
    )
  }

  const rows = data.rows
  if (rows.length === 0) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">数据为空</div>
      </Card>
    )
  }

  const columns = Object.keys(rows[0] ?? {})
  if (columns.length < 2) {
    return (
      <Card title={title} className="basis-full">
        <div className="flex h-[200px] items-center justify-center text-gray-400">数据格式错误</div>
      </Card>
    )
  }
  const xKey = columns[0]!
  const yKey = columns[1]!
  const weekendRanges = getWeekendRanges(rows, data.year ?? new Date().getFullYear())

  return (
    <Card title={title} className="basis-full">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={rows} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.gridColor} />
          <XAxis dataKey={xKey} tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
          <YAxis tick={t.axisTickProps} axisLine={t.axisLineProps} tickLine={t.tickLineProps} />
          <Tooltip
            contentStyle={t.tooltipContentStyle}
            labelStyle={t.tooltipLabelStyle}
            itemStyle={t.tooltipItemStyle}
            cursor={{ stroke: t.cursorColor }}
          />
          <Line
            type="monotone"
            dataKey={yKey}
            stroke="#3b82f6"
            dot={false}
            strokeWidth={2}
            label={{ position: 'top', fontSize: 11, fill: t.axisTickProps.fill }}
          />
          {weekendRanges.map((range, i) => (
            <ReferenceArea key={i} x1={range.start} x2={range.end} fill="rgba(255,0,0,0.1)" />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  )
}
